import { Chip, styled, useTheme } from '@mui/material';
import type { ModelStatus, ProductListResult } from '@partful/shared-types';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Thumbnail } from 'src/components/Thumbnail';
import { spacing } from 'src/constants/spacing';
import { Feature } from 'src/Feature';
import { usePermissions } from 'src/hooks/usePermissions';
import { useProductFeatures } from 'src/hooks/useProductFeatures';
import { useTenant } from 'src/providers/Tenant';
import { ProductCardImageContainer, ProductCardStyles, ProductCardTextContainer, StyledBadge } from '../styles';
import { ProductCardActions } from './ProductCardActions';
import { ProductCardEllipsesDropdown } from './ProductCardEllipsesDropdown';

interface ProductCardProps {
  product: ProductListResult;
}

const StyledModelStatus = styled(StyledBadge)`
  position: absolute;
  top: ${spacing.smallSpacing};
  left: ${spacing.smallSpacing};
`;

export function transformStatusString(modelStatus?: ModelStatus) {
  if (modelStatus === 'COMPLETED') {
    return 'Live';
  }
  return modelStatus?.replace('_', ' ').toLowerCase();
}

export function ProductCard({ product }: Readonly<ProductCardProps>) {
  const {
    productId,
    modelStatus,
    thumbnailUrl,
    name: productName,
    enabledFeatures: productEnabledFeatures,
    configurationExternalID,
  } = product;

  const tenantsProductFeatures = useProductFeatures();
  const { enabledFeatures } = useTenant();
  const productFeatures = [...new Set(tenantsProductFeatures.concat(productEnabledFeatures || []))]; // add any product feature not included at tenant level and make it unique to avoid duplication
  const [productStatusDisplayed, setProductStatusDisplayed] = useState(modelStatus);
  const canDeleteProduct = usePermissions(['delete', 'product']);
  const canUpdateProduct = usePermissions(['update', 'product']);
  const canViewPartsCatalogue = usePermissions(['view', 'product.partsCatalogue']);
  const canViewWorkInstructions = usePermissions(['view', 'product.workInstructions']);
  const configManagement = enabledFeatures.includes('configuration-management');

  const statusString = transformStatusString(productStatusDisplayed);
  const canViewBadges = usePermissions(['view', 'draft']);

  const actionButtonsVisible = productFeatures.length > 1;
  const theme = useTheme();

  // uri encode the product id to be used in the URL as the product name may contain special characters that can break the routing, e.g. slashes
  const urlSafeProductId = encodeURIComponent(productId);

  return (
    <ProductCardStyles
      data-status={productStatusDisplayed}
      data-productname={productName}
      data-testid={`product-card-${configManagement ? configurationExternalID : urlSafeProductId}`}
    >
      <ProductCardImageContainer>
        {actionButtonsVisible ? (
          <Thumbnail url={thumbnailUrl} alt={productName} />
        ) : (
          <NavLink data-testid="card-navlink" to={`/product-library/${urlSafeProductId}`}>
            <Thumbnail url={thumbnailUrl} alt={productName} />
          </NavLink>
        )}
      </ProductCardImageContainer>
      {canViewBadges && <StyledModelStatus status={productStatusDisplayed}>{statusString}</StyledModelStatus>}
      {(canDeleteProduct || canUpdateProduct) &&
        Feature({
          children: product.configurationPID ? (
            /* istanbul ignore next */ <Chip
              component={NavLink}
              to={`/manage-product/${urlSafeProductId}?configurationPID=${product.configurationPID}`}
              clickable
              sx={{
                position: 'absolute',
                top: theme.spacing(2),
                right: theme.spacing(2),
                background: theme.palette.grey[900],
                color: 'white',
                borderRadius: '0.3rem',
                padding: '1rem 0.2rem',
              }}
              label="Manage Product"
            />
          ) : null,
          flag: 'configuration-management',
          alternate: (
            <ProductCardEllipsesDropdown
              product={product}
              productStatusDisplayed={productStatusDisplayed}
              setProductStatusDisplayed={setProductStatusDisplayed}
              canUpdateProduct={canUpdateProduct}
              canDeleteProduct={canDeleteProduct}
            />
          ),
        })}
      <ProductCardTextContainer>
        {actionButtonsVisible ? (
          <>
            <span>{productName}</span>
            <ProductCardActions
              data-testid="product-card-actions"
              productFeatures={productFeatures}
              productId={urlSafeProductId}
              canViewPartsCatalogue={canViewPartsCatalogue}
              canViewWorkInstructions={canViewWorkInstructions}
            />
          </>
        ) : (
          <NavLink data-testid="card-navlink" to={`/product-library/${urlSafeProductId}`}>
            <span>{productName}</span>
          </NavLink>
        )}
      </ProductCardTextContainer>
    </ProductCardStyles>
  );
}
